import * as THREE from "three"

export type WalkableShape = "square" | "triangle" | "circle"

type WalkBounds = {
  minX: number
  maxX: number
  minZ: number
  maxZ: number
}

type WalkPoint = {
  x: number
  z: number
}

export type ProjectedPoint = {
  x: number
  y: number
}

export type ProjectedLine = {
  start: ProjectedPoint
  end: ProjectedPoint
}

export type ProjectedWallGuide = {
  polygon: ProjectedPoint[]
  gridLines: ProjectedLine[]
}

export type ProjectedRoomShellGuide = {
  floor: ProjectedPoint[]
  walls: {
    back: ProjectedWallGuide
    left: ProjectedWallGuide
    right: ProjectedWallGuide
  }
}

export type RoomShellWallDescriptor = {
  key: "back" | "left" | "right"
  width: number
  height: number
  repeatX: number
  repeatY: number
  position: { x: number; y: number; z: number }
  rotation: { x: number; y: number; z: number }
}

export const ROOM_SHELL_DEBUG_GRID_STROKE = "rgba(255, 255, 255, 0.5)"
export const ROOM_SHELL_DEBUG_FLOOR_COLOR = "#00ffc8"
export const ROOM_SHELL_DEBUG_WALL_FILL_OPACITY = 0.08
export const ROOM_SHELL_DEBUG_WALL_GRID_OPACITY = 0.9
export const ROOM_SHELL_DEBUG_FLOOR_OPACITY = 0.18

export const ROOM_SHELL_CAMERA_CONFIG = {
  fov: 35,
  position: { x: 0, y: 7.5, z: 9.5 },
  lookAt: { x: 0, y: 0.9, z: -2.2 },
} as const

export const ROOM_SHELL_WALL_HEIGHT = 4.4
export const ROOM_SHELL_WALL_MARGIN = 0.55
export const ROOM_SHELL_STAGE_Z_OFFSET = 0.45

const SQUARE_BOUNDS: WalkBounds = {
  minX: -3.8,
  maxX: 3.8,
  minZ: -2.5,
  maxZ: 2.5,
}

const TRIANGLE_POINTS = [
  { x: -3.8, z: 2.45 },
  { x: 0, z: -2.65 },
  { x: 3.8, z: 2.45 },
] as const

const CIRCLE_RADIUS = {
  x: 3.4,
  z: 2.4,
} as const

export function getWalkableShapeBounds(shape: WalkableShape): WalkBounds {
  switch (shape) {
    case "square":
      return offsetBounds(SQUARE_BOUNDS)
    case "triangle":
      return offsetBounds({
        minX: TRIANGLE_POINTS[0].x,
        maxX: TRIANGLE_POINTS[2].x,
        minZ: TRIANGLE_POINTS[1].z,
        maxZ: TRIANGLE_POINTS[0].z,
      })
    case "circle":
      return offsetBounds({
        minX: -CIRCLE_RADIUS.x,
        maxX: CIRCLE_RADIUS.x,
        minZ: -CIRCLE_RADIUS.z,
        maxZ: CIRCLE_RADIUS.z,
      })
  }
}

export function createWalkableShapeGeometry(
  shape: WalkableShape
): THREE.BufferGeometry {
  return new THREE.ShapeGeometry(createWalkableShapePath(shape), 64)
}

export function createRoomShellGridTextureCanvas(
  strokeStyle = "rgba(72, 72, 72, 0.18)"
) {
  const canvas = document.createElement("canvas")
  canvas.width = 256
  canvas.height = 256
  const context = canvas.getContext("2d")

  if (!context) {
    return canvas
  }

  context.clearRect(0, 0, canvas.width, canvas.height)
  context.strokeStyle = strokeStyle
  context.lineWidth = 2

  for (let offset = 32; offset < canvas.width; offset += 32) {
    context.beginPath()
    context.moveTo(offset, 0)
    context.lineTo(offset, canvas.height)
    context.stroke()

    context.beginPath()
    context.moveTo(0, offset)
    context.lineTo(canvas.width, offset)
    context.stroke()
  }

  return canvas
}

export function getRoomShellWallDescriptors(
  shape: WalkableShape
): RoomShellWallDescriptor[] {
  const bounds = getWalkableShapeBounds(shape)
  const backWallZ = bounds.minZ - ROOM_SHELL_WALL_MARGIN
  const sideWallWidth = bounds.maxZ - bounds.minZ + ROOM_SHELL_WALL_MARGIN * 2
  const backWallWidth = bounds.maxX - bounds.minX + ROOM_SHELL_WALL_MARGIN * 2
  const leftWallX = bounds.minX - ROOM_SHELL_WALL_MARGIN
  const rightWallX = bounds.maxX + ROOM_SHELL_WALL_MARGIN

  return [
    {
      key: "back",
      width: backWallWidth,
      height: ROOM_SHELL_WALL_HEIGHT,
      repeatX: 8,
      repeatY: 5,
      position: { x: 0, y: ROOM_SHELL_WALL_HEIGHT / 2, z: backWallZ },
      rotation: { x: 0, y: 0, z: 0 },
    },
    {
      key: "left",
      width: sideWallWidth,
      height: ROOM_SHELL_WALL_HEIGHT,
      repeatX: 6,
      repeatY: 5,
      position: { x: leftWallX, y: ROOM_SHELL_WALL_HEIGHT / 2, z: 0 },
      rotation: { x: 0, y: Math.PI / 2, z: 0 },
    },
    {
      key: "right",
      width: sideWallWidth,
      height: ROOM_SHELL_WALL_HEIGHT,
      repeatX: 6,
      repeatY: 5,
      position: { x: rightWallX, y: ROOM_SHELL_WALL_HEIGHT / 2, z: 0 },
      rotation: { x: 0, y: -Math.PI / 2, z: 0 },
    },
  ]
}

export function getRoomShellWallTint(key: RoomShellWallDescriptor["key"]) {
  return key === "back" ? "#8b5cf6" : "#53b1fd"
}

export function projectRoomShellGuide(
  shape: WalkableShape,
  width: number,
  height: number
): ProjectedRoomShellGuide {
  const camera = createStageCamera(width, height)
  const bounds = getWalkableShapeBounds(shape)
  const backWall = getBackWallCorners(bounds)
  const leftWall = getLeftWallCorners(bounds)
  const rightWall = getRightWallCorners(bounds)

  return {
    floor: projectPoints(camera, getFloorOutlinePoints(shape), width, height),
    walls: {
      back: createProjectedWallGuide(camera, backWall, 8, 5, width, height),
      left: createProjectedWallGuide(camera, leftWall, 6, 5, width, height),
      right: createProjectedWallGuide(camera, rightWall, 6, 5, width, height),
    },
  }
}

function createStageCamera(width: number, height: number) {
  const camera = new THREE.PerspectiveCamera(
    ROOM_SHELL_CAMERA_CONFIG.fov,
    width / height,
    0.1,
    100
  )

  camera.position.set(
    ROOM_SHELL_CAMERA_CONFIG.position.x,
    ROOM_SHELL_CAMERA_CONFIG.position.y,
    ROOM_SHELL_CAMERA_CONFIG.position.z
  )
  camera.lookAt(
    ROOM_SHELL_CAMERA_CONFIG.lookAt.x,
    ROOM_SHELL_CAMERA_CONFIG.lookAt.y,
    ROOM_SHELL_CAMERA_CONFIG.lookAt.z
  )
  camera.updateProjectionMatrix()
  camera.updateMatrixWorld(true)

  return camera
}

function createWalkableShapePath(shape: WalkableShape): THREE.Shape {
  const path = new THREE.Shape()

  if (shape === "square") {
    const points = getSquareFloorOutline()
    path.moveTo(points[0].x, -points[0].z)
    for (const point of points.slice(1)) {
      path.lineTo(point.x, -point.z)
    }
    path.closePath()
    return path
  }

  if (shape === "triangle") {
    const points = getTriangleFloorOutline()
    path.moveTo(points[0].x, -points[0].z)
    path.lineTo(points[1].x, -points[1].z)
    path.lineTo(points[2].x, -points[2].z)
    path.closePath()
    return path
  }

  path.absellipse(
    0,
    -ROOM_SHELL_STAGE_Z_OFFSET,
    CIRCLE_RADIUS.x,
    CIRCLE_RADIUS.z,
    0,
    Math.PI * 2,
    false,
    0
  )
  return path
}

function getFloorOutlinePoints(shape: WalkableShape): THREE.Vector3[] {
  if (shape === "square") {
    return getSquareFloorOutline().map((point) => new THREE.Vector3(point.x, 0, point.z))
  }

  if (shape === "triangle") {
    return getTriangleFloorOutline().map(
      (point) => new THREE.Vector3(point.x, 0, point.z)
    )
  }

  const points: THREE.Vector3[] = []
  const segments = 48
  for (let index = 0; index < segments; index += 1) {
    const theta = (index / segments) * Math.PI * 2
    points.push(
      new THREE.Vector3(
        Math.cos(theta) * CIRCLE_RADIUS.x,
        0,
        Math.sin(theta) * CIRCLE_RADIUS.z + ROOM_SHELL_STAGE_Z_OFFSET
      )
    )
  }
  return points
}

function getSquareFloorOutline(): readonly WalkPoint[] {
  return [
    { x: SQUARE_BOUNDS.minX, z: SQUARE_BOUNDS.maxZ + ROOM_SHELL_STAGE_Z_OFFSET },
    { x: SQUARE_BOUNDS.minX, z: SQUARE_BOUNDS.minZ + ROOM_SHELL_STAGE_Z_OFFSET },
    { x: SQUARE_BOUNDS.maxX, z: SQUARE_BOUNDS.minZ + ROOM_SHELL_STAGE_Z_OFFSET },
    { x: SQUARE_BOUNDS.maxX, z: SQUARE_BOUNDS.maxZ + ROOM_SHELL_STAGE_Z_OFFSET },
  ]
}

function getTriangleFloorOutline(): readonly WalkPoint[] {
  return TRIANGLE_POINTS.map((point) => ({
    x: point.x,
    z: point.z + ROOM_SHELL_STAGE_Z_OFFSET,
  }))
}

function getBackWallCorners(bounds: WalkBounds): THREE.Vector3[] {
  const z = bounds.minZ - ROOM_SHELL_WALL_MARGIN
  return [
    new THREE.Vector3(bounds.minX - ROOM_SHELL_WALL_MARGIN, 0, z),
    new THREE.Vector3(bounds.maxX + ROOM_SHELL_WALL_MARGIN, 0, z),
    new THREE.Vector3(
      bounds.maxX + ROOM_SHELL_WALL_MARGIN,
      ROOM_SHELL_WALL_HEIGHT,
      z
    ),
    new THREE.Vector3(
      bounds.minX - ROOM_SHELL_WALL_MARGIN,
      ROOM_SHELL_WALL_HEIGHT,
      z
    ),
  ]
}

function getLeftWallCorners(bounds: WalkBounds): THREE.Vector3[] {
  const x = bounds.minX - ROOM_SHELL_WALL_MARGIN
  return [
    new THREE.Vector3(x, 0, bounds.maxZ + ROOM_SHELL_WALL_MARGIN),
    new THREE.Vector3(x, 0, bounds.minZ - ROOM_SHELL_WALL_MARGIN),
    new THREE.Vector3(
      x,
      ROOM_SHELL_WALL_HEIGHT,
      bounds.minZ - ROOM_SHELL_WALL_MARGIN
    ),
    new THREE.Vector3(
      x,
      ROOM_SHELL_WALL_HEIGHT,
      bounds.maxZ + ROOM_SHELL_WALL_MARGIN
    ),
  ]
}

function getRightWallCorners(bounds: WalkBounds): THREE.Vector3[] {
  const x = bounds.maxX + ROOM_SHELL_WALL_MARGIN
  return [
    new THREE.Vector3(x, 0, bounds.minZ - ROOM_SHELL_WALL_MARGIN),
    new THREE.Vector3(x, 0, bounds.maxZ + ROOM_SHELL_WALL_MARGIN),
    new THREE.Vector3(
      x,
      ROOM_SHELL_WALL_HEIGHT,
      bounds.maxZ + ROOM_SHELL_WALL_MARGIN
    ),
    new THREE.Vector3(
      x,
      ROOM_SHELL_WALL_HEIGHT,
      bounds.minZ - ROOM_SHELL_WALL_MARGIN
    ),
  ]
}

function createProjectedWallGuide(
  camera: THREE.PerspectiveCamera,
  corners: THREE.Vector3[],
  repeatX: number,
  repeatY: number,
  width: number,
  height: number
): ProjectedWallGuide {
  const [bottomLeft, bottomRight, topRight, topLeft] = corners
  const gridLines: ProjectedLine[] = []

  for (let index = 1; index < repeatX; index += 1) {
    const progress = index / repeatX
    gridLines.push({
      start: projectPoint(
        camera,
        interpolateVector(bottomLeft, bottomRight, progress),
        width,
        height
      ),
      end: projectPoint(
        camera,
        interpolateVector(topLeft, topRight, progress),
        width,
        height
      ),
    })
  }

  for (let index = 1; index < repeatY; index += 1) {
    const progress = index / repeatY
    gridLines.push({
      start: projectPoint(
        camera,
        interpolateVector(bottomLeft, topLeft, progress),
        width,
        height
      ),
      end: projectPoint(
        camera,
        interpolateVector(bottomRight, topRight, progress),
        width,
        height
      ),
    })
  }

  return {
    polygon: projectPoints(camera, corners, width, height),
    gridLines,
  }
}

function interpolateVector(start: THREE.Vector3, end: THREE.Vector3, progress: number) {
  return start.clone().lerp(end, progress)
}

function projectPoints(
  camera: THREE.PerspectiveCamera,
  points: THREE.Vector3[],
  width: number,
  height: number
) {
  return points.map((point) => projectPoint(camera, point, width, height))
}

function projectPoint(
  camera: THREE.PerspectiveCamera,
  point: THREE.Vector3,
  width: number,
  height: number
): ProjectedPoint {
  const projected = point.clone().project(camera)
  return {
    x: ((projected.x + 1) / 2) * width,
    y: ((1 - projected.y) / 2) * height,
  }
}

function offsetBounds(bounds: WalkBounds): WalkBounds {
  return {
    minX: bounds.minX,
    maxX: bounds.maxX,
    minZ: bounds.minZ + ROOM_SHELL_STAGE_Z_OFFSET,
    maxZ: bounds.maxZ + ROOM_SHELL_STAGE_Z_OFFSET,
  }
}
